import SplashScreen from '@oas/components/SplashScreen';
import LoadingSpinner from '@ui/components/LoadingSpinner';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import useVersionChecker from '@ui/hooks/useVersionChecker';
import { OAS_APP_VERSION } from '@oas/constants/constants';
import { preserveCurrentQueryParamsInStorage } from '@oas/utils/preserveQueryParams';

// Dynamic Imports
const GlobalProviders = lazy(() => import('@oas/providers/GlobalProviders'));
const AuthenticatedZoneProviders = lazy(
    () => import('@oas/providers/AuthenticatedZoneProviders')
);
const AuthenticatedLayout = lazy(
    () => import('@oas/layout/AuthenticatedLayout')
);
const AccountPage = lazy(() => import('@oas/pages/AccountPage'));
const DashboardPage = lazy(
    () => import('@oas/pages/DashboardPage/DashboardPage')
);
const FrequentlyAskedQuestionsPage = lazy(
    () =>
        import(
            '@oas/pages/FrequentlyAskedQuestionsPage/FrequentlyAskedQuestionsPage'
        )
);
const ManagePaymentMethodsPage = lazy(
    () => import('@oas/pages/PaymentsPage/routes/ManagePaymentMethodsPage')
);
const PaymentAuthorizationPage = lazy(
    () => import('@oas/pages/PaymentsPage/routes/PaymentAuthorizationPage')
);
const PaymentDetailPage = lazy(
    () => import('@oas/pages/PaymentsPage/routes/PaymentDetailPage')
);
const PaymentsLayout = lazy(
    () => import('@oas/pages/PaymentsPage/routes/PaymentsLayout')
);
const SelectPaymentOptionsPage = lazy(
    () => import('@oas/pages/PaymentsPage/routes/SelectPaymentOptionsPage')
);

export const ProtectedRoutes = () => {
    return (
        <Routes>
            <Route path="/" element={<AuthenticatedLayout />}>
                <Route
                    path="/"
                    element={<Navigate to="/dashboard" replace />}
                />
                <Route path="account" element={<AccountPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="faq" element={<FrequentlyAskedQuestionsPage />} />
                <Route path="payments" element={<PaymentsLayout />}>
                    <Route
                        path="/payments"
                        element={<Navigate to="previous-payment" replace />}
                    />
                    <Route
                        path="select-payment-options"
                        element={<SelectPaymentOptionsPage />}
                    />
                    <Route
                        path="previous-payment"
                        element={<PaymentDetailPage />}
                    />
                    <Route
                        path="authorize"
                        element={<PaymentAuthorizationPage />}
                    />
                    <Route
                        path="manage-payment-methods"
                        element={<ManagePaymentMethodsPage />}
                    />
                    <Route path="*" element={<PaymentDetailPage />} />
                </Route>
                <Route path="reset/:resetToken" element={<>Reset</>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
};

export default function App() {
    useVersionChecker(OAS_APP_VERSION, '/account/');
    useEffect(() => {
        preserveCurrentQueryParamsInStorage();
    }, []);
    return (
        <Suspense fallback={<SplashScreen />}>
            <GlobalProviders>
                <Suspense fallback={<LoadingSpinner />}>
                    <AuthenticatedZoneProviders>
                        <ProtectedRoutes />
                    </AuthenticatedZoneProviders>
                </Suspense>
            </GlobalProviders>
        </Suspense>
    );
}
